import { type FC, useState } from "react";
import {
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import { FormattedMessage } from "react-intl";
import { useToken } from "@packsize/authentication";

import { useExternalAccessSubscriptions } from "@/hooks/use-external-access-subscriptions";
import { type ExternalAccessSubscription } from "@/views/configuration/server-settings/types";

import { ApiKeyInfoButton } from "./modals/api-key-info-button";
import { DeleteApiKeyButton } from "./modals/delete-api-key-button";

export const orderScopes = (scope: string[]): string => {
	return scope.sort().join(", ");
};

export const convertApiKeyScopesToList = (uglyScope: string | undefined): string[] => {
	if (!uglyScope) {
		return [];
	}
	const splitScope = uglyScope.split("/");
	const combinedScope = splitScope[splitScope.length - 1];

	return combinedScope.split("And").map((x) => x.split("_")[0]);
};

export const makeScopePrettyAgain = (uglyScope: string | undefined): string => {
	if (!uglyScope || typeof uglyScope !== "string") {
		return "";
	}

	return orderScopes(convertApiKeyScopesToList(uglyScope));
};

const getConnectionType = (subscription: ExternalAccessSubscription): string => {
	return subscription.$type === "ApimSubscriptionMeta" ? "API Key" : "OAuth";
};

const getFormattedScope = (subscription: ExternalAccessSubscription): string => {
	if (subscription.$type === "ApimSubscriptionMeta") {
		return makeScopePrettyAgain(subscription.properties.scope);
	}
	return orderScopes(subscription.properties.scope);
};

const ApiSettingsTab: FC = () => {
	const { decodedToken } = useToken();
	const { data: subscriptions = [], isLoading, isError, refetch } = useExternalAccessSubscriptions(decodedToken?.tid);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const sorted = [...subscriptions].sort(
		(a, b) => new Date(b.properties.createdDate).getTime() - new Date(a.properties.createdDate).getTime(),
	);

	return (
		<>
			<TableContainer sx={{ flex: 1 }}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>
								<FormattedMessage id="Name" defaultMessage="Name" />
							</TableCell>
							<TableCell>
								<FormattedMessage id="Services" defaultMessage="Services" />
							</TableCell>
							<TableCell>
								<FormattedMessage id="Connection Type" defaultMessage="Connection Type" />
							</TableCell>
							<TableCell>
								<FormattedMessage id="Date" defaultMessage="Date" />
							</TableCell>
							<TableCell />
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoading === true && (
							<TableRow sx={{ height: 100 }}>
								<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
									<CircularProgress color="info" data-testid="api-settings-tab__progress__loading" />
								</TableCell>
							</TableRow>
						)}
						{isError === true && isLoading === false && (
							<TableRow sx={{ height: 100 }}>
								<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
									<Grid container justifyContent="center" alignItems="center" gap={2}>
										<Typography component="span" color="error" data-testid="api-settings-tab__text__error">
											<FormattedMessage
												id="Failed to load API connections. Please try again."
												defaultMessage="Failed to load API connections. Please try again."
											/>
										</Typography>
										<IconButton onClick={() => refetch()} data-testid="api-settings-tab__button__retry">
											<Refresh />
										</IconButton>
									</Grid>
								</TableCell>
							</TableRow>
						)}
						{isLoading === false && isError === false && sorted.length === 0 && (
							<TableRow>
								<TableCell colSpan={1000} align="center">
									<Typography color="textDisabled" data-testid="api-settings-tab__text__no-connections">
										<FormattedMessage
											id="You haven't generated any API keys yet"
											defaultMessage="You haven't generated any API keys yet"
										/>
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{isLoading === false &&
							isError === false &&
							sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
								<TableRow key={item.identifier}>
									<TableCell sx={{ fontWeight: 500 }}>{item.properties.displayName}</TableCell>
									<TableCell>{getFormattedScope(item)}</TableCell>
									<TableCell>{getConnectionType(item)}</TableCell>
									<TableCell>
										{new Date(item.properties.createdDate).toLocaleDateString("en-us", {
											year: "2-digit",
											month: "2-digit",
											day: "2-digit",
										})}
									</TableCell>
									<TableCell>
										{item.$type === "ApimSubscriptionMeta" && item.hashedPrimaryKey === undefined && (
											<Tooltip
												title={
													<FormattedMessage
														id="API key valid exclusively in legacy environments"
														defaultMessage="API key valid exclusively in legacy environments"
													/>
												}
											>
												<Chip
													label={<FormattedMessage id="Legacy" defaultMessage="Legacy" />}
													variant="outlined"
													color="error"
												/>
											</Tooltip>
										)}
									</TableCell>
									<TableCell sx={{ width: 0 }}>
										<Stack gap={1} direction="row">
											<ApiKeyInfoButton apiKey={item} />
											<DeleteApiKeyButton apiKey={item} />
										</Stack>
									</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[10, 25, 50]}
				component="div"
				count={sorted.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={(_, pageNumber) => setPage(pageNumber)}
				onRowsPerPageChange={(event) => {
					setRowsPerPage(parseInt(event.target.value));
					setPage(0);
				}}
			/>
		</>
	);
};

export default ApiSettingsTab;
